import styled from '@emotion/styled'
import clsx from 'clsx'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useState, VFC } from 'react'
import { useQuery } from 'react-query'

import { Story, StoryList } from '~/apis/@types'
import Title from '~/components/global/CTitle'
import { DefaultLayout } from '~/components/layout/Default'
import { createApiClient } from '~/utils/createApiClient'

const StoryHeader = styled.div`
    letter-spacing: 1.8px;
`

const StoryImage = styled.div`
    display: flex;
    flex-direction: column;
    height: auto;
    background-color: #ffffff33;
    letter-spacing: 0.1em;
`

export type StorySlugPageViewProps = {
    onClickStory: (articleId: string) => void
    onClickThumbnail: (index: number) => void
    isLoading: boolean
    isSelectThumbnail: number
    storyList?: StoryList
    story?: Story
    active: boolean
}

export const StorySlugPageView: VFC<StorySlugPageViewProps> = ({
    storyList,
    story,
    onClickStory,
    onClickThumbnail,
    active,
    isSelectThumbnail,
}) => {
    return (
        <DefaultLayout pageTitle={'ストーリー'}>
            <div className="md:pb-[6vw] px-4 max-w-[1200px] m-auto flex flex-col justify-center">
                <Title src="/images/title/story.svg" title={'ストーリー'} additionalClassName="hidden md:block" />
                <div className="flex flex-wrap w-full md:mb-14 mb-6 md:mt-16 ">
                    {storyList?.list.length &&
                        storyList?.list.map((it) => (
                            <div
                                className={clsx(
                                    [
                                        'flex justify-center md:px-12 px-4 md:py-1 py-0.5 md:mr-4 mr-2 md:mb-4 mb-2 border-4 cursor-pointer text-xl font-black',
                                    ],
                                    active,
                                    it.uuid === story?.uuid
                                        ? ['bg-c-secondary text-c-primary border-c-secondary']
                                        : ['bg-c-primary text-white border-white bg-opacity-40'],
                                )}
                                key={it.uuid}
                                onClick={() => onClickStory(it.uuid)}>
                                <p className="self-center">{it.episode}</p>
                            </div>
                        ))}
                </div>
                {story && (
                    <>
                        <StoryHeader className="md:mb-8 mb-4 md:p-10 p-4 bg-c-secondary">
                            <div className="flex justify-start md:text-2xl text-xl" key={story.uuid}>
                                <p className="text-c-primary mr-2 font-bold">{story.episode}</p>
                                <p className="font-semibold text-[#333333]">{story.title}</p>
                            </div>
                        </StoryHeader>
                        <StoryImage className="md:p-10 md:mb-8 p-4 mb-4">
                            <div className="relative">
                                {story.thumbnail && <img src={story.thumbnail[isSelectThumbnail]} alt={story.title} />}
                            </div>
                            <div className="grid grid-cols-5 md:gap-4 gap-1 md:mt-4 mt-2">
                                {story.thumbnail.map((it, index) => (
                                    <div
                                        className="cursor-pointer"
                                        key={story.uuid + index}
                                        onClick={() => onClickThumbnail(index)}>
                                        {story.thumbnail && <img src={it} alt={story.title} />}
                                    </div>
                                ))}
                            </div>
                        </StoryImage>
                        <div className="md:p-10 p-4 md:font-light font-normal text-c-normal-text bg-white md:text-xl text-sm md:leading-10 leading-7">
                            <p>{story.description}</p>
                        </div>
                    </>
                )}
            </div>
        </DefaultLayout>
    )
}

export const StorySlugPage: NextPage = () => {
    const apiClient = createApiClient()
    const [findStory, setStory] = useState<Story>()
    const [active, setActive] = useState(false)
    const [isSelectThumbnail, setThumbnail] = useState(0)
    const router = useRouter()

    const { data, isLoading } = useQuery(
        [],
        async () => {
            const { body } = await apiClient.api.story.get()
            setStory(body.list[0])
            // １話も取得できなかったらTOPにリダイレクト
            if (body.list.length === 0) {
                await router.push({
                    pathname: '/',
                })
            }
            return body
        },
        {
            keepPreviousData: false,
        },
    )

    const onClickStory = (storyId: string) => {
        const selectId = storyId
        const findStory = data?.list.find((it) => it.uuid === selectId)
        setStory(findStory)
        setActive(!active)
    }

    const onClickThumbnail = (index: number) => {
        setThumbnail(index)
    }

    return (
        <StorySlugPageView
            storyList={data}
            onClickStory={onClickStory}
            onClickThumbnail={onClickThumbnail}
            isLoading={isLoading}
            story={findStory}
            active={active}
            isSelectThumbnail={isSelectThumbnail}
        />
    )
}

export default StorySlugPage
